import React, { useEffect, useState } from 'react'
import { useForm } from 'antd/es/form/Form'
import { Button, Col, Form, Input, Modal, Row, Typography, Upload, UploadFile, UploadProps } from 'antd'
import FormItem from 'antd/es/form/FormItem'
import { useTranslation } from 'react-i18next'
import { UploadOutlined } from '@ant-design/icons'
import { useDispatch } from 'react-redux'
import { Transaction, Voucher } from '../types'
import TransactionTableForm from './TransactionTableForm'
import { transformFieldData } from '../../../../utils/form'
import { notificationAction } from '../../../../redux/middleware/actions'
import { Attachment } from '../../../../types/attachment/attachment'
import { useResetFormOnCloseModal } from '../../../../components/Form/hooks'
import { DatePicker } from '../../../../components/Form'
import { toFormDate } from '../../../../config/dayjsConfig'
import { useDeleteVoucherAttachment } from '../../../../features/vouchers/queries/useDeleteVoucherAttachment'
import { getVoucherAttachment } from '../../../../features/vouchers/service/voucherService'
import { useDeleteConfirmModal } from '../../../../shared/modals/deleteConfirmModal'

export type TransactionFormFields = Pick<Transaction, 'description' | 'debit' | 'credit' | 'accountCode'>
export type VoucherFormFields = Pick<Voucher, 'description' | 'id'> & {
  transactions: TransactionFormFields[]
  date: string
}
const { Title } = Typography

const formStyle: React.CSSProperties = {
  maxWidth: 'none',
  padding: 24
}

const attachmentsToUploadfiles = (attachments?: Attachment[]) => {
  if (!attachments) return []
  const data = attachments?.map(
    attachment =>
      ({
        name: attachment.filename,
        uid: attachment.id
      } satisfies UploadFile)
  )

  return data
}

interface VoucherFormProps {
  open: boolean
  voucher: Voucher | null
  loading?: boolean
  copyVoucher?: boolean
  handleClose: () => void
  onCreate: (values: FormData) => void
  onUpdate: (values: FormData) => void
}

const VoucherForm: React.FC<VoucherFormProps> = ({
  open,
  copyVoucher,
  voucher,
  loading,
  onCreate,
  onUpdate,
  handleClose
}) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const [form] = useForm<VoucherFormFields>()
  const [fileList, setFileList] = useState<UploadFile[]>([])

  const deleteAttachmentMutation = useDeleteVoucherAttachment()
  const showDeleteConfirm = useDeleteConfirmModal()

  useResetFormOnCloseModal({
    form,
    open
  })

  useEffect(() => {
    if (voucher) {
      form.setFieldsValue(
        transformFieldData(voucher, {
          date: (val: string) => toFormDate(val)
        })
      )
      const attachmentFiles = attachmentsToUploadfiles(voucher?.attachments)
      setFileList(attachmentFiles)
    }
  }, [voucher, form, copyVoucher])

  const deleteAttachment = (attachmentId: string) => {
    if (!voucher?.id) {
      throw new Error('Voucher ID is required for deleting attachments')
    }
    if (!voucher?.companyId) {
      throw new Error('Company ID is required for deleting attachments')
    }

    return deleteAttachmentMutation.mutateAsync({
      voucherId: voucher.id.toString(),
      attachmentId,
      companyId: voucher.companyId
    })
  }

  const uploadProps: UploadProps = {
    async onRemove(file) {
      // For existing attachments (those without originFileObj)
      if (!file.originFileObj) {
        // If copying, just remove locally - don't delete from original voucher
        if (copyVoucher) {
          setFileList(prev => prev.filter(f => f.uid !== file.uid))
          return true
        }

        // For editing existing voucher, show confirmation and delete from server
        showDeleteConfirm({
          onOk: async () => {
            try {
              await deleteAttachment(file.uid)
              setFileList(prev => prev.filter(f => f.uid !== file.uid))
            } catch (error) {
              // Error is already handled by the mutation's onError callback
            }
          }
        })
        return false // Let modal handle the deletion
      }

      // For new files, just remove from list
      setFileList(prev => prev.filter(f => f.uid !== file.uid))
      return true
    },
    beforeUpload: file => {
      setFileList(prev => [
        ...prev,
        {
          uid: file.uid || crypto.randomUUID(),
          name: file.name,
          type: file.type,
          size: file.size,
          originFileObj: file
        } as UploadFile
      ])

      return false
    },
    fileList
  }

  return (
    <Modal
      title={t('global:voucher')}
      width={1100}
      open={open}
      onCancel={() => {
        handleClose()
        setFileList([])
      }}
      confirmLoading={loading}
      onOk={async () => {
        try {
          const values = await form.validateFields()
          const formData = new FormData()

          values.id && !copyVoucher && formData.append('id', values.id.toString())
          formData.append('description', values.description)
          formData.append('date', values.date)

          values.transactions.forEach((transaction, index) => {
            Object.entries(transaction).forEach(([key, value]) => {
              if (value && !(copyVoucher && (key === 'id' || key === 'guid' || key === 'voucherGuid'))) {
                formData.append(`transactions[${index}][${key}]`, value?.toString())
              }
            })
          })

          // Handle file uploads
          const filePromises = fileList.map(async file => {
            // Check if it's a File object directly (new upload)
            if (file instanceof File) {
              return { file, type: 'new' }
            }

            if (file.originFileObj) {
              // New file upload (UploadFile with originFileObj)
              return { file: file.originFileObj, type: 'new' }
            }
            if (copyVoucher && file.uid && voucher?.id && voucher?.companyId) {
              // Copy existing attachment by fetching its content
              try {
                const arrayBuffer = await getVoucherAttachment({
                  voucherId: voucher.id.toString(),
                  attachmentId: file.uid,
                  companyId: voucher.companyId
                })

                // Find attachment info to get filename and contentType
                const attachment = voucher.attachments?.find(att => att.id === file.uid)
                const filename = attachment?.filename || 'attachment'
                const contentType = attachment?.contentType || 'application/octet-stream'

                // Create File object from attachment data
                const fileBlob = new File([arrayBuffer], filename, { type: contentType })
                return { file: fileBlob, type: 'copied' }
              } catch (error) {
                console.error('Failed to fetch attachment for copying:', error)
                return null
              }
            }
            return null
          })

          const resolvedFiles = await Promise.all(filePromises)
          resolvedFiles.forEach(result => {
            if (result?.file) {
              formData.append('files[]', result.file)
            }
          })

          if (copyVoucher || !values.id) {
            await onCreate(formData)
          } else {
            await onUpdate(formData)
          }

          setFileList([])
        } catch (error) {
          dispatch(
            notificationAction({
              type: 'error',
              message: 'ERROR',
              description: (error as Error).message
            })
          )
        }
      }}
      okText={t('global:save')}
      maskClosable={false}
    >
      <Form
        form={form}
        style={formStyle}
        layout="vertical"
        onValuesChange={(val: Partial<VoucherFormFields>) => {
          const transaction = val?.transactions?.filter(Boolean)?.[0]
          if (transaction?.credit || transaction?.debit) {
            form.validateFields()
          }
        }}
      >
        <Title level={5}>{t('vouchersPage:voucherInfo')}</Title>
        <Row gutter={24}>
          <FormItem name="id" hidden>
            <Input />
          </FormItem>
          <Col span={8}>
            <FormItem name="description" label={t('vouchersPage:voucherDesc')} rules={[{ required: true }]}>
              <Input />
            </FormItem>
          </Col>
          <Col span={8}>
            {/* TODO: translaatio globaliin  */}
            <FormItem name="date" label={t('contractsPage:date')} rules={[{ required: true }]}>
              <DatePicker style={{ width: '100%' }} />
            </FormItem>
          </Col>
          <Col span={8}>
            <Form.Item label={t('global:attachment')}>
              <Upload {...uploadProps}>
                <Button icon={<UploadOutlined />}>{t('vouchersPage:selectAttachment')}</Button>
              </Upload>
            </Form.Item>
          </Col>
        </Row>
        <TransactionTableForm copyVoucher={copyVoucher} />
      </Form>
    </Modal>
  )
}

export default VoucherForm
